"use client";

import { cn } from "@/lib/utils";
import getImagesFromProduct from "@/utils/elevate/getImageInfoFromProduct";
import { useMarket } from "@app/_context";
import useTranslate from "@app/_hooks/useTranslate";
import useAddCartItem from "@features/cart/hooks/useAddToCart";
import { Button } from "@ui/button";
import { Loader2 } from "lucide-react";
import type { ComponentProps, MouseEvent } from "react";
import ProductCard from "src/components/product-card/ProductCard";
import { WishlistProduct } from "../../utils/sortWishlistItems";
import WishlistItemTagMenu from "./WishlistItemTagMenu";
import WishlistItemToggle from "./WishlistItemToggle";

export default function WishlistItem({
  wishlistItem,
  tagId,
  readOnly = false,
  className,
  ...props
}: {
  wishlistItem: WishlistProduct;
  tagId?: number;
  readOnly?: boolean;
} & ComponentProps<"article">) {
  const { product } = wishlistItem;
  const { market } = useMarket().state;
  const t = useTranslate();
  const { mutate, isPending, isSuccess } = useAddCartItem();

  if (!product) {
    return null;
  }

  const [variant] = product.variants;

  if (!variant) {
    return null;
  }

  const [image] = getImagesFromProduct(product);

  async function onClick(event: MouseEvent<HTMLButtonElement>) {
    if (!product) return;

    event.stopPropagation();
    event.preventDefault();

    void mutate([{ quantity: 1, product }]);
  }

  return (
    <ProductCard
      className={cn("@container/wishlist-item relative", className)}
      {...props}
    >
      {!readOnly && !tagId && (
        <div className="absolute top-2 left-2 z-10">
          <WishlistItemTagMenu item={wishlistItem.wishlistItem} />
        </div>
      )}
      <ProductCard.Link href={variant.link} locale={market.locale}>
        <ProductCard.Image
          src={image?.src}
          alt={product.title}
          width={375}
          height={375}
          className={cn(
            "aspect-square w-full object-contain mix-blend-multiply",
          )}
        >
          {!readOnly && (
            <ProductCard.Meta position="top-right">
              <WishlistItemToggle
                item={wishlistItem}
                tagId={tagId}
                className="absolute top-2 right-2"
              />
            </ProductCard.Meta>
          )}
        </ProductCard.Image>
        <ProductCard.Content>
          <ProductCard.Brand>{product.brand}</ProductCard.Brand>
          <ProductCard.Title>{product.title}</ProductCard.Title>
          <ProductCard.Price product={product} />
          <Button
            onClick={onClick}
            className="mt-4 w-full"
            variant={isSuccess ? "secondary" : "outline"}
          >
            {isPending ? (
              <Loader2 className="size-4 animate-spin" strokeWidth={2} />
            ) : isSuccess ? (
              t("added")
            ) : (
              t("addToCart")
            )}
          </Button>
        </ProductCard.Content>
      </ProductCard.Link>
    </ProductCard>
  );
}
